/**
 * Service layer — Tenancy domain
 *
 * Two layers:
 *
 * A) Browser reads via Supabase anon key → SECURITY DEFINER RPC functions.
 *    Only these three functions make direct Supabase calls:
 *      loadCurrentUserSeat, canAccess, getUserTenant
 *
 * B) Admin writes via Vercel API routes that use SUPABASE_SERVICE_ROLE_KEY.
 *    All admin functions pass the Clerk session token in Authorization header.
 *
 * USAGE:
 *   // At app startup (OutlineView):
 *   await loadCurrentUserSeat(user.id)
 *
 *   // In any component (synchronous, no async needed):
 *   if (hasModule('admin')) { ... }
 */

import { supabase } from '../lib/supabase.js'

/* ── Module cache ─────────────────────────────────────────────────────────── */

// Populated once by loadCurrentUserSeat. null = no seat found or error.
// Shape when populated: { seatType: {id, slug, name}, modules: string[] }
let _seatCache = undefined  // undefined = not yet loaded, null = loaded but no seat
let _seatLoaded = false

// Populated alongside the seat (same startup call). null = no tenant found or error.
// Shape when populated: { id, name, slug, homeOrganizationAirtableId }
let _tenantCache = undefined

/* ── A) Browser-side reads ───────────────────────────────────────────────── */

/**
 * Load the current user's seat and module list. Call once at app startup.
 * Populates the module cache used by hasModule().
 *
 * @param {string} clerkUserId — from useUser().user.id
 * @returns {Promise<{seatType, modules} | null>}
 */
export async function loadCurrentUserSeat(clerkUserId) {
  if (!supabase || !clerkUserId) {
    _seatLoaded = true
    return null
  }
  // Fire the tenant lookup alongside the seat lookup (same startup call, same clerk user id) so
  // getCurrentTenant() is available synchronously wherever hasModule()/getCurrentSeat() already are.
  getUserTenant(clerkUserId).then(t => { _tenantCache = t }).catch(() => { _tenantCache = null })
  try {
    const { data, error } = await supabase.rpc('get_user_seat', {
      p_clerk_user_id: clerkUserId,
    })
    _seatCache  = error || !data ? null : data
    _seatLoaded = true
    return _seatCache
  } catch (err) {
    console.warn('[tenancy] loadCurrentUserSeat failed:', err)
    _seatLoaded = true
    return null
  }
}

/**
 * Returns true if the current user's seat has access to the given module.
 * Synchronous — only valid after loadCurrentUserSeat() has resolved.
 *
 * @param {string} moduleSlug — 'schedule' | 'design' | 'production' | 'admin'
 * @returns {boolean}
 */
export function hasModule(moduleSlug) {
  if (!_seatCache || !Array.isArray(_seatCache.modules)) return false
  return _seatCache.modules.includes(moduleSlug)
}

/**
 * Returns the full cached seat object, or null if no seat assigned.
 * @returns {{ seatType: {id, slug, name}, modules: string[] } | null}
 */
export function getCurrentSeat() {
  return _seatCache || null
}

/**
 * True if the current user's seat is 'manager' or 'admin' — the two "manager and up" tiers.
 * Synchronous — only valid after loadCurrentUserSeat() has resolved (same convention as
 * hasModule()). Used to gate manager-and-up-only features like /debug.
 * @returns {boolean}
 */
export function isManagerOrAbove() {
  const slug = _seatCache && _seatCache.seatType && _seatCache.seatType.slug
  return slug === 'manager' || slug === 'admin'
}

/**
 * True if the current user's seat is exactly 'admin' — narrower than isManagerOrAbove().
 * Synchronous — only valid after loadCurrentUserSeat() has resolved. Used to gate
 * admin-exclusive actions (e.g. overriding an already-approved drawing's PDF).
 * @returns {boolean}
 */
export function isAdmin() {
  const slug = _seatCache && _seatCache.seatType && _seatCache.seatType.slug
  return slug === 'admin'
}

/**
 * Returns the current user's cached tenant record (populated alongside the seat at startup), or
 * null if not yet resolved / no tenant found. Synchronous — safe to call anywhere after
 * loadCurrentUserSeat() has been kicked off, same convention as getCurrentSeat().
 * @returns {{ id, name, slug, homeOrganizationAirtableId } | null}
 */
export function getCurrentTenant() {
  return _tenantCache || null
}

/**
 * True once loadCurrentUserSeat has completed (successfully or not).
 * Use this to show a loading state before rendering gated content.
 * @returns {boolean}
 */
export function isSeatLoaded() {
  return _seatLoaded
}

/**
 * One-off async access check. Prefer hasModule() for UI gating after startup.
 *
 * @param {string} clerkUserId
 * @param {string} moduleSlug
 * @returns {Promise<boolean>}
 */
export async function canAccess(clerkUserId, moduleSlug) {
  if (!supabase || !clerkUserId) return false
  try {
    const { data, error } = await supabase.rpc('can_access', {
      p_clerk_user_id: clerkUserId,
      p_module_slug:   moduleSlug,
    })
    return error ? false : !!data
  } catch {
    return false
  }
}

/**
 * Returns the tenant record for the current user.
 *
 * @param {string} clerkUserId
 * @returns {Promise<{id, name, slug} | null>}
 */
export async function getUserTenant(clerkUserId) {
  if (!supabase || !clerkUserId) return null
  try {
    const { data, error } = await supabase.rpc('get_user_tenant', {
      p_clerk_user_id: clerkUserId,
    })
    return error || !data ? null : data
  } catch {
    return null
  }
}

/* ── B) Admin writes via Vercel API routes ───────────────────────────────── */

/**
 * Fetches a Clerk session token via the callback stored by OutlineView.
 * Falls back to null if not yet set or unavailable.
 */
async function getClerkToken() {
  if (typeof window.__clerkGetToken !== 'function') return null
  try { return await window.__clerkGetToken() } catch { return null }
}

async function adminFetch(path, options = {}) {
  const token = await getClerkToken()
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

/**
 * List all users with seat assignments for the given tenant.
 * @param {string} tenantId
 */
export function listUsers(tenantId) {
  return adminFetch(`/api/admin/tenancy/users?tenantId=${encodeURIComponent(tenantId)}`)
}

/**
 * Set a member's BQE Core resource id (writes the Airtable Member's `BQE Contact ID`).
 * Pass the memberId (from listUsers) when known; email is a fallback to resolve the Member.
 */
export function setUserBqeCoreId(memberId, email, value) {
  return adminFetch('/api/admin/tenancy/users', {
    method: 'POST',
    body: JSON.stringify({ action: 'setBqeCoreId', memberId, email, value }),
  })
}

/**
 * Change a member's employment type (employee | contractor). Writes the same per-member time
 * policy Admin → Time edits — contractors attest daily instead of meeting the hours minimum.
 */
export function setUserEmployment(tenantId, memberId, employmentType) {
  return adminFetch('/api/admin/tenancy/users', {
    method: 'POST',
    body: JSON.stringify({ action: 'setEmployment', tenantId, memberId, employmentType }),
  })
}

/**
 * Set a person's Practice Position (writes their Airtable Contact). The position is what gives them
 * a bill rate — no position means every hour they log is valued at $0. Resolves { billRate }.
 */
export function setUserPosition(email, positionId, memberId) {
  return adminFetch('/api/admin/tenancy/users', {
    method: 'POST',
    body: JSON.stringify({ action: 'setPosition', email, memberId, positionId }),
  })
}

/**
 * Assign (or change) a seat type for a user within a tenant.
 * @param {string} tenantId
 * @param {string} clerkUserId
 * @param {string} seatTypeId
 */
export function assignSeat(tenantId, clerkUserId, seatTypeId) {
  return adminFetch('/api/admin/tenancy/users', {
    method: 'POST',
    body: JSON.stringify({ tenantId, clerkUserId, seatTypeId }),
  })
}

/**
 * Remove a user's seat assignment (revoke access).
 * @param {string} tenantId
 * @param {string} clerkUserId
 */
export function removeSeat(tenantId, clerkUserId) {
  return adminFetch('/api/admin/tenancy/users', {
    method: 'DELETE',
    body: JSON.stringify({ tenantId, clerkUserId }),
  })
}

/**
 * List all seat types (with their module assignments) for a tenant.
 * @param {string} tenantId
 */
export function listSeatTypes(tenantId) {
  return adminFetch(`/api/admin/tenancy/seat-types?tenantId=${encodeURIComponent(tenantId)}`)
}

/**
 * Module access granted to ONE PERSON, on top of whatever their seat carries.
 *
 * Used for the permissions that should not ride along with a seat — Bookkeeping above all, which
 * carries payroll, owner draws and distributions. Access is the union of seat grants and person
 * grants, so a grant only ever widens one person and a revoke never touches anyone else.
 */
export function listModuleGrants(tenantId) {
  return adminFetch(`/api/admin/tenancy/grants?tenantId=${encodeURIComponent(tenantId)}`)
}

/** Grant one module to one person. `note` records WHY, for whoever reads the row later. */
export function grantModule(tenantId, clerkUserId, moduleSlug, note) {
  return adminFetch('/api/admin/tenancy/grants', {
    method: 'POST',
    body: JSON.stringify({ tenantId, clerkUserId, moduleSlug, note }),
  })
}

/** Revoke one person's grant. Their seat is untouched. */
export function revokeModule(tenantId, clerkUserId, moduleSlug) {
  return adminFetch('/api/admin/tenancy/grants', {
    method: 'DELETE',
    body: JSON.stringify({ tenantId, clerkUserId, moduleSlug }),
  })
}

/** List all available modules. */
export function listModules() {
  return adminFetch('/api/admin/tenancy/modules')
}

/**
 * Send an invite email to a new team member.
 * @param {string} tenantId
 * @param {string} email
 * @param {string} seatTypeId
 * @param {string} [employmentType] — 'employee' | 'contractor' (seeds the time-gate policy on accept)
 */
export function sendInvite(tenantId, email, seatTypeId, employmentType = 'employee') {
  return adminFetch('/api/admin/tenancy/invite', {
    method: 'POST',
    body: JSON.stringify({ tenantId, email, seatTypeId, employmentType }),
  })
}

/**
 * List all invites (pending and accepted) for the tenant.
 * @param {string} tenantId
 */
export function listInvites(tenantId) {
  return adminFetch(`/api/admin/tenancy/invites?tenantId=${encodeURIComponent(tenantId)}`)
}

/**
 * Revoke a pending invite.
 * @param {string} tenantId
 * @param {string} inviteId
 */
export function revokeInvite(tenantId, inviteId) {
  return adminFetch(`/api/admin/tenancy/invites?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'DELETE',
    body: JSON.stringify({ inviteId }),
  })
}

/**
 * Revoke an existing pending invite and immediately send a fresh one to the same address.
 * @param {string} tenantId
 * @param {{ id, email, seat_types: {id}, employment_type }} invite
 */
export async function resendInvite(tenantId, invite) {
  await revokeInvite(tenantId, invite.id)
  return sendInvite(tenantId, invite.email, invite.seat_types.id, invite.employment_type || 'employee')
}

/* ── C) Auto-provisioning for invited users ──────────────────────────────── */

/**
 * Called when a user has no seat after login. If they have a pending invite
 * (matched by their Clerk email), this creates their seat automatically.
 *
 * Returns the provisioned seat data, or null if no invite was found.
 * @returns {Promise<{seatType, modules} | null>}
 */
export async function provisionFromInvite() {
  const token = await getClerkToken()
  if (!token) return null
  try {
    const res = await fetch('/api/tenancy/provision', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.seat) {
      // Populate the module cache so hasModule() works immediately
      _seatCache  = data.seat
      _seatLoaded = true
    }
    return data.seat || null
  } catch {
    return null
  }
}
